import { createAsyncThunk, createAction, createSlice } from '@reduxjs/toolkit';
import { twpApiURL } from 'src/api.js';
import { target } from 'models/routes';
import defaultWizardSteps from 'models/payrollSteps';
import { completeTransformer } from 'helpers/completeTransformer';
import { downloadFile } from 'src/helpers/core/downloads';
import { getURL } from 'helpers/routeHelper';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { hasValue, isNil } from 'swipeclock-frontend';
import { AdminRoles } from 'models/masterdata';
import { accrualsFormats } from 'src/helpers/formatting';
import { createSelector } from 'reselect';

export const getTimecardExport = createAsyncThunk(
  'payrollExport/getTimecardExport',
  async (_args, { dispatch, getState, rejectWithValue }) => {
    dispatch(setExporting({ exporting: true }));
    const user = await getState().user;
    const api = twpApiURL(user);
    const { selectedPayPeriod, exportOptions: formats } = await getState()
      .payrollExport;
    const { start, end } = selectedPayPeriod;

    try {
      const promises = Object.keys(formats)
        .filter((e) => formats[e])
        .map((e) => api.fetchPayrollActivities(selectedPayPeriod, e));
      const allResults = await Promise.all(promises);
      const results = allResults.reduce((prev, curr) => {
        // We add to the data we get from API call. Then...
        // We add format and payPeriod so when it goes
        // to the finalize call it can then easily construct
        // names for each data blob and then download it to
        // the user's machine
        const { format } = queryString.parse(curr?.config?.url);
        const payload = { data: curr?.data, format, payPeriod: { start, end } };
        return [...prev, payload];
      }, []);
      dispatch(setExporting({ exporting: false }));
      return { list: results };
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

export const getPayPeriods = createAsyncThunk(
  'payrollExport/getPayPeriods',
  async (_, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const api = twpApiURL(user);
    const count = 3;

    try {
      const data = await api.getPayPeriods(count);

      return data;
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

export const getFinalizePayPeriods = createAsyncThunk(
  'finalizePayPeriod/getFinalizePayPeriods',
  async (_, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const { end } = await getState().payrollExport?.selectedPayPeriod;
    const api = twpApiURL(user);

    try {
      const response = await api.getFinalizedPayPeriods();
      const isFinalized = completeTransformer(response, end);

      return isFinalized;
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

const payrollExportSlice = createSlice({
  name: 'payrollExport',
  initialState: {
    payPeriods: undefined,
    payrollSteps: undefined,
    selectedPayPeriodKey: undefined,
    selectedPayPeriod: undefined,
    subTitle: undefined,
    title: undefined,
    wizardCompleted: false,
    wizardContent: {
      header: null,
      id: null,
      src: null,
      scrolling: 'yes',
    },
    wizardReady: false,
    wizardRunning: false,
    wizardStep: 0,
    wizardSteps: undefined,
    exportDisabled: true,
    exporting: false,
    exportOptions: {
      sumtwe: false,
      csvdetailtwe: false,
    },
  },
  reducers: {
    cancelWizard(state) {
      state.wizardRunning = false;
    },
    runExportValidation(state) {
      state.exportDisabled = !Object.values(state.exportOptions).reduce(
        (result, curr) => {
          return result || curr;
        }
      );
    },
    setExporting(state, action) {
      state.exporting = action?.payload?.exporting;
    },
    setExportOptions(state, action) {
      state.exportOptions = {
        ...state.exportOptions,
        ...action?.payload?.options,
      };
    },
    setSelectedPayPeriod(state, action) {
      state.selectedPayPeriodKey = action?.payload?.selectedPayPeriodKey;
      state.selectedPayPeriod = action?.payload?.selectedPayPeriod;
    },
    setSubTitle(state, action) {
      state.subTitle = action?.payload?.subTitle;
    },
    setTitle(state, action) {
      state.title = action?.payload?.title;
    },
    setWizardContent(state, action) {
      const {
        payload: { url, route, header, target: wizardTarget },
      } = action;
      const id = hasValue(route)
        ? `${route}-${header?.title}`
        : `component-${header?.title}`;

      state.wizardContent = {
        ...state.wizardContent,
        header,
        id: `ifr-${id}`,
        src: url,
        target: wizardTarget,
        route,
      };
    },
    setWizardStep(state, action) {
      state.wizardStep = action?.payload?.step;
    },
    setWizardStepsOnInitialize(state, action) {
      state.wizardSteps = action?.payload?.steps;
    },
    startWizard(state) {
      state.wizardRunning = true;
    },
    stopExporting(state) {
      state.exporting = 0;
    },
    setWizardCompleted(state, action) {
      state.wizardCompleted = action?.payload?.completed;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFinalizePayPeriods.fulfilled, (state, action) => {
        state.selectedPayPeriod = {
          ...state.selectedPayPeriod,
          isFinalized: action.payload,
        };
      })
      .addCase(getTimecardExport.fulfilled, (state, action) => {
        const { list } = action?.payload;
        list.forEach((result) => {
          const { data, payPeriod, format } = result;
          const { start, end } = payPeriod ?? {};
          downloadFile(data?.FormatString, `${format}_${start}_${end}`);
        });
      })
      .addCase(getPayPeriods.fulfilled, (state, action) => {
        // StartDate: "2021-01-11",
        // EndDate: "2021-01-31",
        // PrevStartDate: "20221-01-01"
        state.payPeriods = payPeriodTransformer(action?.payload);
      });
  },
});

export function initializePayrollExport({ features, roles }) {
  return async (dispatch) => {
    const evaluator = rulesEvaluator(features, roles);
    const steps = payrollStepTransformer(evaluator);
    dispatch(
      setWizardStepsOnInitialize({ steps: steps.transform(defaultWizardSteps) })
    );
  };
}

export function selectPayPeriodForPayroll(key) {
  return async (dispatch, getState) => {
    const { payPeriods } = await getState().payrollExport;
    dispatch(
      setSelectedPayPeriod({
        selectedPayPeriodKey: key,
        selectedPayPeriod: payPeriods?.exportablePayPeriods[key],
      })
    );
  };
}

export function switchWizardStep(anchor, header) {
  return async (dispatch, getState) => {
    const { token } = await getState().user;
    const { selectedPayPeriod } = await getState().payrollExport;
    if (anchor?.target === target.self) {
      const url = getURL({ token, anchor, selectedPayPeriod });

      dispatch(setWizardContent({ header, ...url, ...anchor }));
      return;
    }
    if (anchor?.target === target.blank && hasValue(window)) {
      const { url } = getURL({ token, anchor, selectedPayPeriod });
      window.open(url, '_blank');
      dispatch(setWizardContent({ rel: null, url: null }));
      return;
    }
    if (anchor?.target === target.local) {
      dispatch(setWizardContent({ header, ...anchor }));
      return;
    }

    // No content found - so don't load any content ( Blank Screen )
    dispatch(setWizardContent({ rel: null, url: null }));
  };
}

export function handleNextWizardStep() {
  return async (dispatch, getState) => {
    const { wizardStep, wizardSteps } = await getState().payrollExport;
    if (wizardStep < wizardSteps.length - 1) {
      dispatch(setWizardStep({ step: wizardStep + 1 }));
    }
  };
}

export function handlePrevWizardStep() {
  return async (dispatch, getState) => {
    const { wizardStep } = await getState().payrollExport;
    if (wizardStep > 0) {
      dispatch(setWizardStep({ step: wizardStep - 1 }));
    } else if (wizardStep === 0) {
      dispatch(cancelWizard());
    }
  };
}

// Note: This is temp for right now to have
// links that need to be opened but are deeply nested
// inside iframes.
export function openAnchorInNewTab(anchor) {
  return async (dispatch, getState) => {
    const { token } = await getState().user;
    if (anchor?.target === target.blank && hasValue(window)) {
      const { url } = getURL({ token, anchor });
      window.open(url, '_blank');
    }
  };
}

export const externalOnNavigateRequested = createAction(
  'external/onNavigateRequested'
);

export function externalOnNavigateRequestedFailure() {
  return (dispatch) => {
    dispatch(
      openAnchorInNewTab({
        route: 'integrations',
        target: 'blank',
      })
    );
  };
}

export const {
  cancelWizard,
  runExportValidation,
  stopExporting,
  setExporting,
  setExportOptions,
  setSelectedPayPeriod,
  setSubTitle,
  setTitle,
  setWizard,
  setWizardContent,
  setWizardCompleted,
  setWizardStep,
  setWizardStepsOnInitialize,
  startWizard,
} = payrollExportSlice.actions;

export const payrollExportSelector = (state) => state.payrollExport;

export const payrollExportDataSelector = createSelector(
  [payrollExportSelector],
  (payrollExport) => ({
    subTitle: payrollExport.subTitle,
    selectedPayPeriod: payrollExport.selectedPayPeriod,
    title: payrollExport.title,
    wizardCompleted: payrollExport.wizardCompleted,
    wizardContent: payrollExport.wizardContent,
    wizardRunning: payrollExport.wizardRunning,
    wizardStep: payrollExport.wizardStep,
    wizardSteps: payrollExport.wizardSteps,
  })
);

export const payperiodSelector = createSelector(
  [payrollExportSelector],
  (payrollExport) => payrollExport.payPeriods
);

export function usePayperiodSelector() {
  const payPeriods = useSelector(payperiodSelector);
  return payPeriods;
}

export default payrollExportSlice.reducer;

// ##########################################
// ### Private Helper Methods
// ##########################################

function rulesEvaluator(features, roles) {
  // Helper Methods for rules

  // featureIds:
  // It's a dictionary: key - featureCategoryId, value - count
  const featureIds = features?.reduce((ids, feature) => {
    const key = feature?.id || 0;
    const count = hasValue(ids[key]) ? ids[key] + 1 : 1;
    return { ...ids, [key]: count };
  }, {});

  const isAdmin = () => {
    return AdminRoles?.some((adminRole) => {
      const index = roles?.findIndex((x) => x?.roleType === adminRole);
      return index > -1;
    });
  };

  const hasSomeAdminRoles = (r) =>
    r?.some((role) => {
      const index = AdminRoles?.findIndex((x) => x === role);
      return index > -1;
    });

  const hasSomeFeatureIds = (ids) => !!ids?.some((id) => featureIds[id]);

  // Public methods to run rules an evaluate

  function checkIfPayPeriodStepIncluded(item) {
    const { featureIds: itemIds, roles: itemRoles } = item ?? {};
    const hasFeatureId = hasSomeFeatureIds(itemIds);
    const hasPermission = isAdmin() && hasSomeAdminRoles(itemRoles);
    const isIncluded = isNil(itemIds) && isNil(itemRoles);
    return hasFeatureId || hasPermission || isIncluded;
  }

  return {
    checkIfPayPeriodStepIncluded,
  };
}

function payrollStepTransformer(evaluator) {
  function transform(steps) {
    if (!hasValue(steps)) {
      return null;
    }

    return steps.reduce((prevList, item) => {
      return evaluator?.checkIfPayPeriodStepIncluded(item)
        ? [...prevList, transformItem(item)]
        : prevList;
    }, []);
  }

  function transformItem(item) {
    return {
      ...item,
      key: item.title.toLowerCase(),
    };
  }

  return {
    transform,
  };
}

function payPeriodTransformer(data) {
  if (hasValue(data)) {
    const format = accrualsFormats.formatDateForPayrollReview;
    const payPeriods = data.map((payPeriod, index) => {
      const { BeginDate } = payPeriod;
      const { EndDate } = payPeriod;
      const { Year, Month, Day } = BeginDate;
      const { Year: EndYear, Month: EndMonth, Day: EndDay } = EndDate;
      const currentPayPeriod = index < 1;

      if (currentPayPeriod) {
        return {
          start: format(`${Month}/${Day}/${Year}`),
          end: format(`${EndMonth}/${EndDay}/${EndYear}`),
          label: 'Current',
          key: `current-start-${Month}-${Day}-${Year}-end-${EndMonth}-${EndDay}-${EndYear}`,
        };
      }

      return {
        start: format(`${Month}/${Day}/${Year}`),
        end: format(`${EndMonth}/${EndDay}/${EndYear}`),
        label: 'Previous',
        key: `previous-start-${Month}-${Day}-${Year}-end-${EndMonth}-${EndDay}-${EndYear}`,
      };
    });

    const [current, previous1, previous2] = payPeriods;

    return { ...data, exportablePayPeriods: { current, previous1, previous2 } };
  }
  return data;
}
